import {
  AccessTime as AccessTimeIcon,
  AttachMoney as AttachMoneyIcon,
  CheckCircle as CheckCircleIcon,
  Leaderboard as LeaderboardIcon
} from '@mui/icons-material';
import { Alert, Box, CircularProgress, Container, Grid, IconButton, LinearProgress, Paper, Tooltip, Typography } from '@mui/material';
import React, { useMemo, useState } from 'react';
import { subWeeks, startOfWeek, endOfWeek } from 'date-fns';
import FiltersBar from '../components/FiltersBar.jsx';
import FaturamentoRankingModal from '../components/cards/FaturamentoRankingModal';
import IndicadorRankingModal from '../components/cards/IndicadorRankingModal';
import KpiCard from '../components/cards/KpiCard';
import DisponibilidadeTurnoContratosTable from '../components/tables/DisponibilidadeTurnoContratosTable.jsx';
import FaturamentoContratosTable from '../components/tables/FaturamentoContratosTable.jsx';
import TempoMovimentosContratosTable from '../components/tables/TempoMovimentosContratosTable.jsx';
import TempoNaoApontadoContratosTable from '../components/tables/TempoNaoApontadoContratosTable.jsx';
import TempoSaidaBaseContratosTable from '../components/tables/TempoSaidaBaseContratosTable.jsx';
import { useMultipleQueries } from '../hooks/useApiCache';



const Dashboard = () => {

  // Calcular última semana (semana anterior a atual) para inicialização das datas
  const calcularUltimaSemana = () => {
    const hoje = new Date();
    const semanaAnterior = subWeeks(hoje, 1);
    return {
      inicio: startOfWeek(semanaAnterior, { weekStartsOn: 0 }),
      fim: endOfWeek(semanaAnterior, { weekStartsOn: 0 })
    };
  };
  const { inicio: ultimaSemanaInicio, fim: ultimaSemanaFim } = calcularUltimaSemana();

  // Estados dos filtros - inicializados com "Todas as opções"
  const [contrato, setContrato] = useState(['Todos']);
  const [centro, setCentro] = useState(['Todos']);
  const [equipe, setEquipe] = useState(['Todos']);
  const [startDate, setStartDate] = useState(ultimaSemanaInicio);
  const [endDate, setEndDate] = useState(ultimaSemanaFim);
  const [openRankingModal, setOpenRankingModal] = useState(false);
  const [openAderenciaRankingModal, setOpenAderenciaRankingModal] = useState(false);
  const [openSaidaBaseRankingModal, setOpenSaidaBaseRankingModal] = useState(false);
  const [openTurnoRankingModal, setOpenTurnoRankingModal] = useState(false);
  const [openExecucaoRankingModal, setOpenExecucaoRankingModal] = useState(false);
  const [openDeslocamentoRankingModal, setOpenDeslocamentoRankingModal] = useState(false);
  const [openNaoApontadoRankingModal, setOpenNaoApontadoRankingModal] = useState(false);
  const [shouldLoadData, setShouldLoadData] = useState(true); // Carrega automaticamente ao abrir
  const [hasLoadedOnce, setHasLoadedOnce] = useState(false);
  const [loadCounter, setLoadCounter] = useState(0);

  // Objeto de filtros reutilizável
  const filters = useMemo(() => ({ 
    startDate, 
    endDate, 
    contrato, 
    centro, 
    equipe
  }), [startDate, endDate, contrato, centro, equipe]);

  // Filtros para ranking (sempre com 'Todos')
  const rankingFilters = useMemo(() => ({
    startDate,
    endDate,
    contrato: 'Todos',
    centro: 'Todos',
    equipe: 'Todos'
  }), [startDate, endDate]);

  // Requisições controladas pelo botão "Carregar"
  const queryOptions = {
    enabled: shouldLoadData,
    staleTime: 0, // Dados sempre considerados stale (velhos)
    gcTime: 0, // Não mantém no cache
    refetchOnMount: true,
    refetchOnWindowFocus: false
  };

  const { data, isLoading } = useMultipleQueries([
    // KPIs principais
    { endpoint: '/producao-prevista-realizada-rev1', filters, options: { ...queryOptions, _key: loadCounter } },
    { endpoint: '/percentual-turnos', filters, options: { ...queryOptions, _key: loadCounter } },
    { endpoint: '/tempo-medio-saida-base', filters, options: { ...queryOptions, _key: loadCounter } },
    { endpoint: '/tempos-movimentos', filters, options: { ...queryOptions, _key: loadCounter } },
    
    // Tabelas por contrato
    { endpoint: '/producao-prevista-realizada-rev1-contrato', filters, options: { ...queryOptions, _key: loadCounter } },
    { endpoint: '/percentual-turnos-contrato', filters, options: { ...queryOptions, _key: loadCounter } },
    { endpoint: '/tempo-medio-saida-base-contrato', filters, options: { ...queryOptions, _key: loadCounter } },
    { endpoint: '/tempos-movimentos-contrato', filters, options: { ...queryOptions, _key: loadCounter } },
    
    // Dados para ranking (sempre 'Todos')
    { endpoint: '/producao-prevista-realizada-rev1-contrato', filters: rankingFilters, options: { ...queryOptions, _key: loadCounter } },
    { endpoint: '/percentual-turnos-contrato', filters: rankingFilters, options: { ...queryOptions, _key: loadCounter } },
    { endpoint: '/tempo-medio-saida-base-contrato', filters: rankingFilters, options: { ...queryOptions, _key: loadCounter } },
    { endpoint: '/tempos-movimentos-contrato', filters: rankingFilters, options: { ...queryOptions, _key: loadCounter } }
  ]);

  // Atualizar hasLoadedOnce quando terminar de carregar
  React.useEffect(() => {
    if (shouldLoadData && !isLoading) {
      setHasLoadedOnce(true);
      setShouldLoadData(false); // Reset para permitir novo carregamento
    }
  }, [shouldLoadData, isLoading]);

  // Desestruturar todos os dados de uma vez
  const [
    faturamentoData = [],
    aderenciaData = [],
    saidaBaseData = [],
    turnoData = [],
    faturamentoContratos = [],
    disponibilidadeContratos = [],
    tempoSaidaBaseContratos = [],
    tempoMovimentosContratos = [],
    rankingFaturamento = [],
    rankingAderencia = [],
    rankingSaida = [],
    rankingTempos = []
  ] = data;

  // Processar dados de ranking a partir dos dados cacheados
  const rankingData = useMemo(() => {
    const fatData = Array.isArray(rankingFaturamento) ? rankingFaturamento : [];
    const aderData = Array.isArray(rankingAderencia) ? rankingAderencia : [];
    const saidaData = Array.isArray(rankingSaida) ? rankingSaida : [];
    const temposData = Array.isArray(rankingTempos) ? rankingTempos : [];

    const contratos = new Set();
    fatData.forEach(item => contratos.add(item.des_contrato));
    aderData.forEach(item => contratos.add(item.des_contrato));
    saidaData.forEach(item => contratos.add(item.des_contrato));
    temposData.forEach(item => contratos.add(item.des_contrato));

    return Array.from(contratos).map(contrato => {
      const fat = fatData.find(i => i.des_contrato === contrato) || {};
      const ader = aderData.find(i => i.des_contrato === contrato) || {};
      const saida = saidaData.find(i => i.des_contrato === contrato) || {};
      const tempos = temposData.find(i => i.des_contrato === contrato) || {};
      return {
        num_contrato: contrato,
        indicadores: {
          'Faturamento': fat.percentual_realizado ?? null,
          'Aderência de Turno': ader.percentual_realizado ?? null,
          'Tempo Médio de Saída da Base': saida.tempo_medio_saida_base_minutos ?? null,
          'Tempo Médio de Execução': tempos.tempo_medio_servico ?? null,
          'Tempo Médio de Deslocamento': tempos.tempo_medio_deslocamento ?? null,
          'Tempo Médio do Turno': tempos.tempo_medio_turno ?? null,
          'Tempo Médio Não Apontado': tempos.tempo_medio_nao_apontado ?? null
        }
      };
    });
  }, [rankingFaturamento, rankingAderencia, rankingSaida, rankingTempos]);

  // KPIs calculados a partir dos dados do cache
  const updatedKpiData = useMemo(() => [
    // 1. Faturamento
    {
      title: 'Faturamento',
      value: isLoading ? '...' : (() => {
        // Suporta tanto objeto quanto array
        const data = Array.isArray(faturamentoData) ? faturamentoData[0] : faturamentoData;
        if (!data || Object.keys(data).length === 0) return '--';
        const valor = Number(data['producao_realizada']);
        if (isNaN(valor)) return '--';
        return (valor / 1000000).toFixed(2);
      })(),
      unit: 'MR$',
      target: '100%',
      progress: isLoading ? 0 : (() => {
        const data = Array.isArray(faturamentoData) ? faturamentoData[0] : faturamentoData;
        if (!data || Object.keys(data).length === 0) return 0;
        const valor = Number(data['percentual_realizado']);
        if (isNaN(valor)) return 0;
        return valor;
      })(),
      chipValue: isLoading ? '--' : (() => {
        const data = Array.isArray(faturamentoData) ? faturamentoData[0] : faturamentoData;
        if (!data || Object.keys(data).length === 0) return '--';
        const valor = Number(data['percentual_realizado']);
        if (isNaN(valor)) return '--';
        return `${valor}%`;
      })(),
      trend: isLoading ? '' : (() => {
        const data = Array.isArray(faturamentoData) ? faturamentoData[0] : faturamentoData;
        if (!data || Object.keys(data).length === 0) return '';
        const valor = Number(data['percentual_realizado']);
        if (isNaN(valor)) return '';
        if (valor > 100) return 'up';
        if (valor < 100) return 'down';
        return '';
      })(),
      trendValue: isLoading ? '' : (() => {
        const data = Array.isArray(faturamentoData) ? faturamentoData[0] : faturamentoData;
        if (!data || Object.keys(data).length === 0) return '';
        const valor = Number(data['percentual_realizado']);
        if (isNaN(valor)) return '';
        const diff = valor - 100;
        return diff > 0 ? `+${diff.toFixed(2)}%` : `${diff.toFixed(2)}%`;
      })(),
      color: 'info',
      icon: AttachMoneyIcon,
      loading: isLoading
    },
    // 2. Aderência de Turno
    {
      title: 'Aderência de Turno',
      value: isLoading ? '...' : (() => {
        const data = Array.isArray(aderenciaData) ? aderenciaData[0] : aderenciaData;
        if (!data || Object.keys(data).length === 0) return '--';
        const valor = Number(data['percentual_realizado']);
        if (isNaN(valor)) return '--';
        return `${valor.toFixed(2)}%`;
      })(),
      unit: '%',
      target: '100%',
      progress: isLoading ? 0 : (() => {
        const data = Array.isArray(aderenciaData) ? aderenciaData[0] : aderenciaData;
        if (!data || Object.keys(data).length === 0) return 0;
        const valor = Number(data['percentual_realizado']);
        if (isNaN(valor)) return 0;
        return valor;
      })(),
      chipValue: isLoading ? '--' : (() => {
        const data = Array.isArray(aderenciaData) ? aderenciaData[0] : aderenciaData;
        if (!data || Object.keys(data).length === 0) return '--';
        const valor = Number(data['percentual_realizado']);
        if (isNaN(valor)) return '--';
        return `${valor.toFixed(2)}%`;
      })(),
      trend: isLoading ? '' : (() => {
        const data = Array.isArray(aderenciaData) ? aderenciaData[0] : aderenciaData;
        if (!data || Object.keys(data).length === 0) return '';
        const valor = Number(data['percentual_realizado']);
        if (isNaN(valor)) return '';
        if (valor > 100) return 'up';
        if (valor < 100) return 'down';
        return '';
      })(),
      trendValue: isLoading ? '' : (() => {
        const data = Array.isArray(aderenciaData) ? aderenciaData[0] : aderenciaData;
        if (!data || Object.keys(data).length === 0) return '';
        const valor = Number(data['percentual_realizado']);
        if (isNaN(valor)) return '';
        const diff = valor - 100;
        return diff > 0 ? `+${diff.toFixed(2)}%` : `${diff.toFixed(2)}%`;
      })(),
      color: 'info',
      icon: CheckCircleIcon,
      loading: isLoading
    },
    // 3. Tempo Médio de Saída da Base
    {
      title: 'Tempo Médio de Saída da Base',
      value: isLoading ? '...' : (() => {
        const data = Array.isArray(saidaBaseData) ? saidaBaseData[0] : saidaBaseData;
        if (!data || Object.keys(data).length === 0) return '--';
        const valor = Number(data['tempo_medio_saida_base_minutos']);
        if (isNaN(valor)) return '--';
        return valor.toFixed(2);
      })(),
      unit: 'min',
      target: '15 min',
      progress: isLoading ? 0 : (() => {
        const data = Array.isArray(saidaBaseData) ? saidaBaseData[0] : saidaBaseData;
        if (!data || Object.keys(data).length === 0) return 0;
        const valor = Number(data['tempo_medio_saida_base_minutos']);
        if (isNaN(valor)) return 0;
        return Math.min((valor / 15) * 100, 100);
      })(),
      chipValue: isLoading ? '--' : (() => {
        const data = Array.isArray(saidaBaseData) ? saidaBaseData[0] : saidaBaseData;
        if (!data || Object.keys(data).length === 0) return '--';
        const valor = Number(data['tempo_medio_saida_base_minutos']);
        if (isNaN(valor)) return '--';
        return `${valor.toFixed(2)} min`;
      })(),
      trend: isLoading ? '' : (() => {
        const data = Array.isArray(saidaBaseData) ? saidaBaseData[0] : saidaBaseData;
        if (!data || Object.keys(data).length === 0) return '';
        const valor = Number(data['tempo_medio_saida_base_minutos']);
        if (isNaN(valor)) return '';
        if (valor < 15) return 'up';
        if (valor > 15) return 'down';
        return '';
      })(),
      trendValue: isLoading ? '' : (() => {
        const data = Array.isArray(saidaBaseData) ? saidaBaseData[0] : saidaBaseData;
        if (!data || Object.keys(data).length === 0) return '';
        const valor = Number(data['tempo_medio_saida_base_minutos']);
        if (isNaN(valor)) return '';
        const diff = valor - 15;
        return diff > 0 ? `+${diff.toFixed(2)} min` : `${diff.toFixed(2)} min`;
      })(),
      color: 'info',
      icon: AccessTimeIcon,
      loading: isLoading
    },
    // 4. Tempo Médio do Turno
    {
      title: 'Tempo Médio do Turno',
      value: isLoading ? '...' : (() => {
        const data = Array.isArray(turnoData) ? turnoData[0] : turnoData;
        if (!data || Object.keys(data).length === 0) return '--';
        const valor = Number(data['tempo_medio_turno']);
        if (isNaN(valor)) return '--';
        return valor.toFixed(2);
      })(),
      unit: 'min',
      target: '480 min',
      progress: isLoading ? 0 : (() => {
        const data = Array.isArray(turnoData) ? turnoData[0] : turnoData;
        if (!data || Object.keys(data).length === 0) return 0;
        const valor = Number(data['tempo_medio_turno']);
        if (isNaN(valor)) return 0;
        return Math.min((valor / 480) * 100, 100);
      })(),
      chipValue: isLoading ? '--' : (() => {
        const data = Array.isArray(turnoData) ? turnoData[0] : turnoData;
        if (!data || Object.keys(data).length === 0) return '--';
        const valor = Number(data['tempo_medio_turno']);
        if (isNaN(valor)) return '--';
        return `${valor.toFixed(2)} min`;
      })(),
      trend: isLoading ? '' : (() => {
        const data = Array.isArray(turnoData) ? turnoData[0] : turnoData;
        if (!data || Object.keys(data).length === 0) return '';
        const valor = Number(data['tempo_medio_turno']);
        if (isNaN(valor)) return '';
        if (valor < 480) return 'up';
        if (valor > 480) return 'down';
        return '';
      })(),
      trendValue: isLoading ? '' : (() => {
        const data = Array.isArray(turnoData) ? turnoData[0] : turnoData;
        if (!data || Object.keys(data).length === 0) return '';
        const valor = Number(data['tempo_medio_turno']);
        if (isNaN(valor)) return '';
        const diff = valor - 480;
        return diff > 0 ? `+${diff.toFixed(2)} min` : `${diff.toFixed(2)} min`;
      })(),
      color: 'info',
      icon: AccessTimeIcon,
      loading: isLoading
    },
    // 5. Tempo Médio Não Apontado
    {
      title: 'Tempo Médio Não Apontado',
      value: isLoading ? '...' : (() => {
        const data = Array.isArray(turnoData) ? turnoData[0] : turnoData;
        if (!data || Object.keys(data).length === 0) return '--';
        const valor = Number(data['tempo_medio_nao_apontado']);
        if (isNaN(valor)) return '--';
        return valor.toFixed(2);
      })(),
      unit: 'min',
      target: '5 min',
      progress: isLoading ? 0 : (() => {
        const data = Array.isArray(turnoData) ? turnoData[0] : turnoData;
        if (!data || Object.keys(data).length === 0) return 0;
        const valor = Number(data['tempo_medio_nao_apontado']);
        if (isNaN(valor)) return 0;
        return Math.min((valor / 5) * 100, 100);
      })(),
      chipValue: isLoading ? '--' : (() => {
        const data = Array.isArray(turnoData) ? turnoData[0] : turnoData;
        if (!data || Object.keys(data).length === 0) return '--';
        const valor = Number(data['tempo_medio_nao_apontado']);
        if (isNaN(valor)) return '--';
        return `${valor.toFixed(2)} min`;
      })(),
      trend: isLoading ? '' : (() => {
        const data = Array.isArray(turnoData) ? turnoData[0] : turnoData;
        if (!data || Object.keys(data).length === 0) return '';
        const valor = Number(data['tempo_medio_nao_apontado']);
        if (isNaN(valor)) return '';
        if (valor < 5) return 'up';
        if (valor > 5) return 'down';
        return '';
      })(),
      trendValue: isLoading ? '' : (() => {
        const data = Array.isArray(turnoData) ? turnoData[0] : turnoData;
        if (!data || Object.keys(data).length === 0) return '';
        const valor = Number(data['tempo_medio_nao_apontado']);
        if (isNaN(valor)) return '';
        const diff = valor - 5;
        return diff > 0 ? `+${diff.toFixed(2)} min` : `${diff.toFixed(2)} min`;
      })(),
      color: 'info',
      icon: AccessTimeIcon,
      loading: isLoading
    },
    // 6. Tempo Médio de Execução
    {
      title: 'Tempo Médio de Execução',
      value: isLoading ? '...' : (() => {
        const data = Array.isArray(turnoData) ? turnoData[0] : turnoData;
        if (!data || Object.keys(data).length === 0) return '--';
        const valor = Number(data['tempo_medio_servico']);
        if (isNaN(valor)) return '--';
        return valor.toFixed(2);
      })(),
      unit: 'min',
      target: '30 min',
      hasMeta: false,
      color: 'info',
      icon: AccessTimeIcon,
      loading: isLoading
    },
    // 7. Tempo Médio de Deslocamento
    {
      title: 'Tempo Médio de Deslocamento',
      value: isLoading ? '...' : (() => {
        const data = Array.isArray(turnoData) ? turnoData[0] : turnoData;
        if (!data || Object.keys(data).length === 0) return '--';
        const valor = Number(data['tempo_medio_deslocamento']);
        if (isNaN(valor)) return '--';
        return valor.toFixed(2);
      })(),
      unit: 'min',
      target: '60 min',
      hasMeta: false,
      color: 'info',
      icon: AccessTimeIcon,
      loading: isLoading
    },
  ], [isLoading, faturamentoData, aderenciaData, saidaBaseData, turnoData]);

  // Função utilitária para calcular rankings
  function calcularRankings(data, indicadores) {
    const rankings = {};
    const invertidos = [
      'Tempo Médio de Saída da Base',
      'Tempo Médio de Execução',
      'Tempo Médio de Deslocamento',
      'Tempo Médio do Turno',
      'Tempo Médio Não Apontado'
    ];
    indicadores.forEach((ind) => {
      // Array de valores válidos
      const valores = data
        .map((row) => ({ contrato: row.num_contrato, valor: row.indicadores[ind] }))
        .filter((x) => typeof x.valor === 'number' && !isNaN(x.valor));
      // Ordenar: maior-melhor (default), menor-melhor se invertido
      valores.sort((a, b) =>
        invertidos.includes(ind) ? a.valor - b.valor : b.valor - a.valor
      );
      valores.forEach((item, idx) => {
        if (!rankings[item.contrato]) rankings[item.contrato] = {};
        rankings[item.contrato][ind] = idx + 1;
      });
    });
    return rankings;
  }

  // Função para calcular ranking geral ponderado
  function calcularRankingGeral(data, indicadores, rankings) {
    // Pesos definidos pelo usuário
    const pesos = {
      'Aderência de Turno': 0.10,
      'Faturamento': 0.50,
      'Tempo Médio de Saída da Base': 0.15,
      'Tempo Médio de Execução': 0.05,
      'Tempo Médio de Deslocamento': 0.05,
      'Tempo Médio do Turno': 0.05,
      'Tempo Médio Não Apontado': 0.10
    };
    const scores = {};
    data.forEach((row) => {
      let score = 0;
      indicadores.forEach((ind) => {
        const rank = rankings?.[row.num_contrato]?.[ind];
        if (rank) score += (1 / rank) * (pesos[ind] || 0);
      });
      scores[row.num_contrato] = score;
    });
    // Ordenar do maior para o menor (maior score = melhor)
    const ordenados = Object.entries(scores)
      .sort((a, b) => b[1] - a[1])
      .map(([contrato]) => contrato);
    const rankingsGerais = {};
    ordenados.forEach((contrato, idx) => {
      rankingsGerais[contrato] = idx + 1;
    });
    return rankingsGerais;
  }

  // Calcular rankings e ranking geral
  const indicadoresRanking = [
    'Aderência de Turno',
    'Faturamento',
    'Tempo Médio de Saída da Base',
    'Tempo Médio de Execução',
    'Tempo Médio de Deslocamento',
    'Tempo Médio do Turno',
    'Tempo Médio Não Apontado'
  ];
  const rankings = calcularRankings(rankingData, indicadoresRanking);
  const rankingsGerais = calcularRankingGeral(rankingData, indicadoresRanking, rankings);

  // Ordenar os dados pelo ranking geral antes de passar para a tabela
  // eslint-disable-next-line no-unused-vars
  const rankingDataOrdenado = [...rankingData].sort((a, b) => {
    const rankA = rankingsGerais[a.num_contrato] || 9999;
    const rankB = rankingsGerais[b.num_contrato] || 9999;
    return rankA - rankB;
  });

  return (
    <Container maxWidth="xl" sx={{ mb: 4 }}>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" gutterBottom>
          Dashboard Operacional
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Visão geral da performance das equipes em campo
        </Typography>
      </Box>

      {/* Filtros Modernos */}
      <FiltersBar
        contrato={contrato}
        setContrato={setContrato}
        centro={centro}
        setCentro={setCentro}
        startDate={startDate}
        setStartDate={setStartDate}
        endDate={endDate}
        setEndDate={setEndDate}
        equipe={equipe}
        setEquipe={setEquipe}
        onCarregar={() => {
          setShouldLoadData(true);
          setLoadCounter(prev => prev + 1);
        }}
        availablePeriods={['ultima-semana', 'ultimo-mes']}
        periodoSelecionado="ultima-semana"
        maxDateRange={31}
      />

      {/* Barra de Progresso durante carregamento */}
      {isLoading && shouldLoadData && (
        <Paper elevation={3} sx={{ mb: 4, p: 3, borderRadius: 2 }}>
          <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <CircularProgress size={20} />
            Carregando dados...
          </Typography>
          <LinearProgress sx={{ mt: 2, height: 8, borderRadius: 1 }} />
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            Buscando todos os dados dos filtros selecionados. Por favor, aguarde.
          </Typography>
        </Paper>
      )}

      {/* Mensagem quando nada foi carregado ainda */}
      {!shouldLoadData && !hasLoadedOnce && (
        <Alert severity="info" sx={{ mb: 4 }}>
          <Typography variant="body1">
            <strong>Preencha os filtros e clique em "Carregar"</strong> para visualizar os dados do dashboard.
          </Typography>
        </Alert>
      )}

      {/* KPIs - Só mostrar após carregar */}
      {!isLoading && hasLoadedOnce && (
        <>
          <Box sx={{ mb: 4 }}>
            <Grid container spacing={2} sx={{ mb: 2 }}>
              {updatedKpiData.map((kpi, index) => {
                let action = null;
                if (kpi.title === 'Faturamento') {
                  action = (
                    <Tooltip title="Ver Top 5 e Bottom 5 Contratos em Faturamento">
                      <IconButton size="small" onClick={() => setOpenRankingModal(true)}>
                        <LeaderboardIcon fontSize="small" color="primary" />
                      </IconButton>
                    </Tooltip>
                  );
                } else if (kpi.title === 'Aderência de Turno') {
                  action = (
                    <Tooltip title="Ver Top 5 e Bottom 5 em Aderência de Turno">
                      <IconButton size="small" onClick={() => setOpenAderenciaRankingModal(true)}>
                        <LeaderboardIcon fontSize="small" color="primary" />
                      </IconButton>
                    </Tooltip>
                  );
                } else if (kpi.title === 'Tempo Médio de Saída da Base') {
                  action = (
                    <Tooltip title="Ver Top 5 e Bottom 5 em Tempo de Saída da Base">
                      <IconButton size="small" onClick={() => setOpenSaidaBaseRankingModal(true)}>
                        <LeaderboardIcon fontSize="small" color="primary" />
                      </IconButton>
                    </Tooltip>
                  );
                } else if (kpi.title === 'Tempo Médio do Turno') {
                  action = (
                    <Tooltip title="Ver Top 5 e Bottom 5 em Tempo de Turno">
                      <IconButton size="small" onClick={() => setOpenTurnoRankingModal(true)}>
                        <LeaderboardIcon fontSize="small" color="primary" />
                      </IconButton>
                    </Tooltip>
                  );
                } else if (kpi.title === 'Tempo Médio de Execução') {
                  action = (
                    <Tooltip title="Ver Top 5 e Bottom 5 em Tempo de Execução">
                      <IconButton size="small" onClick={() => setOpenExecucaoRankingModal(true)}>
                        <LeaderboardIcon fontSize="small" color="primary" />
                      </IconButton>
                    </Tooltip>
                  );
                } else if (kpi.title === 'Tempo Médio de Deslocamento') {
                  action = (
                    <Tooltip title="Ver Top 5 e Bottom 5 em Tempo de Deslocamento">
                      <IconButton size="small" onClick={() => setOpenDeslocamentoRankingModal(true)}>
                        <LeaderboardIcon fontSize="small" color="primary" />
                      </IconButton>
                    </Tooltip>
                  );
                } else if (kpi.title === 'Tempo Médio Não Apontado') {
                  action = (
                    <Tooltip title="Ver Top 5 e Bottom 5 em Tempo Não Apontado">
                      <IconButton size="small" onClick={() => setOpenNaoApontadoRankingModal(true)}>
                        <LeaderboardIcon fontSize="small" color="primary" />
                      </IconButton>
                    </Tooltip>
                  );
                }
                return (
                  <Grid item xs={12} sm={6} md={3} key={index}>
                    <KpiCard {...kpi} action={action} />
                  </Grid>
                );
              })}
            </Grid>
          </Box>

          {/* Faturamento por Contrato */}
          <Box sx={{ mb: 4 }}>
            <FaturamentoContratosTable
              data={faturamentoContratos}
              loading={isLoading}
            />
          </Box>

          {/* Disponibilidade de Turno por Contrato */}
          <Box sx={{ mb: 4 }}>
            <DisponibilidadeTurnoContratosTable
              data={disponibilidadeContratos}
              loading={isLoading}
            />
          </Box>

          {/* Tempo de Saída da Base por Contrato */}
          <Box sx={{ mb: 4 }}>
            <TempoSaidaBaseContratosTable
              data={tempoSaidaBaseContratos}
              loading={isLoading}
            />
          </Box>

          {/* Tempo Médio Não Apontado por Contrato */}
          <Box sx={{ mb: 4 }}>
            <TempoNaoApontadoContratosTable
              data={tempoMovimentosContratos}
              loading={isLoading}
            />
          </Box>

          {/* Tempo e Movimentos por Contrato */}
          <Box sx={{ mb: 4 }}>
            <TempoMovimentosContratosTable
              data={tempoMovimentosContratos}
              loading={isLoading}
            />
          </Box>
        </>
      )}

      {/* Modal de Ranking de Faturamento */}
      <FaturamentoRankingModal
        open={openRankingModal}
        onClose={() => setOpenRankingModal(false)}
        rankingData={rankingData}
      />

      {/* Modal de Ranking de Aderência de Turno */}
      <IndicadorRankingModal
        open={openAderenciaRankingModal}
        onClose={() => setOpenAderenciaRankingModal(false)}
        rankingData={rankingData}
        indicador="Aderência de Turno"
        tipoOrdenacao="maior-melhor"
      />

      {/* Modal de Ranking de Tempo de Saída da Base */}
      <IndicadorRankingModal
        open={openSaidaBaseRankingModal}
        onClose={() => setOpenSaidaBaseRankingModal(false)}
        rankingData={rankingData}
        indicador="Tempo Médio de Saída da Base"
        tipoOrdenacao="menor-melhor"
      />

      {/* Modal de Ranking de Tempo de Turno */}
      <IndicadorRankingModal
        open={openTurnoRankingModal}
        onClose={() => setOpenTurnoRankingModal(false)}
        rankingData={rankingData}
        indicador="Tempo Médio do Turno"
        tipoOrdenacao="menor-melhor"
      />

      {/* Modal de Ranking de Tempo de Execução */}
      <IndicadorRankingModal
        open={openExecucaoRankingModal}
        onClose={() => setOpenExecucaoRankingModal(false)}
        rankingData={rankingData}
        indicador="Tempo Médio de Execução"
        tipoOrdenacao="menor-melhor"
      />

      {/* Modal de Ranking de Tempo de Deslocamento */}
      <IndicadorRankingModal
        open={openDeslocamentoRankingModal}
        onClose={() => setOpenDeslocamentoRankingModal(false)}
        rankingData={rankingData}
        indicador="Tempo Médio de Deslocamento"
        tipoOrdenacao="menor-melhor"
      />

      {/* Modal de Ranking de Tempo Não Apontado */}
      <IndicadorRankingModal
        open={openNaoApontadoRankingModal}
        onClose={() => setOpenNaoApontadoRankingModal(false)}
        rankingData={rankingData}
        indicador="Tempo Médio Não Apontado"
        tipoOrdenacao="menor-melhor"
      />
    </Container>
  );
};

export default Dashboard;
